import { ApiError } from '@studio/api-client';
import type { ErrorCode } from '@studio/contract';

/**
 * Frontend-friendly copy for the contract's ErrorCode catalog. Server
 * messages are accurate but engineer-flavored ("Generation upstream call
 * timed out."); these strings are written for an e-commerce seller looking
 * at the Studio. Codes we don't have specific copy for fall back to the
 * server-provided `body.message`.
 */
const FRIENDLY: Partial<Record<ErrorCode, string>> = {
  UNAUTHENTICATED: 'Sign-in needed. Sign in to continue.',
  SESSION_EXPIRED: 'Session expired — sign back in to keep going.',
  FORBIDDEN_WORKSPACE: "Your account isn't bound to this workspace yet.",
  INVALID_REQUEST: "Something in the request didn't match what the studio expected.",
  INVALID_PROMPT: 'That prompt was blocked by the safety filter. Try rewording it.',
  INVALID_IMAGE: 'That image was rejected. Try a different photo.',
  GENERATION_FAILED: "The studio couldn't finish that generation. Try again or tweak the prompt.",
  GENERATION_TIMEOUT: 'The generation took too long and timed out. Try again.',
  GENERATION_UPSTREAM_5XX: 'The image provider had a hiccup. Try again in a moment.',
  GENERATION_UPSTREAM_4XX: 'The image provider rejected that request — try a simpler prompt.',
  INSUFFICIENT_CREDITS: "You're out of credits for this task.",
  ADAPTER_UPSTREAM_5XX: 'The connected platform is having trouble. Try again shortly.',
  ADAPTER_RATE_LIMITED: 'The connected platform is rate-limiting us. Try again in a minute.',
  MEDIA_TOO_LARGE: 'That image is bigger than the platform allows.',
  PRODUCT_NOT_FOUND: "We couldn't find that product. It may have been removed.",
  INTERNAL_ERROR: 'Something broke on our side. Try again shortly.',
  SERVICE_UNAVAILABLE: 'The studio is temporarily unavailable. Hang tight.',
};

/**
 * Format any error from a TanStack Query hook into a single user-facing
 * line. ApiError → friendly copy keyed on the code; everything else →
 * `error.message`. Used by Products + Gallery error boxes and the Studio
 * modal Retry banner.
 */
export function humanizeError(err: unknown): string {
  if (err instanceof ApiError) {
    return FRIENDLY[err.body.code] ?? err.body.message;
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong.';
}
